/**
 * App Layout Component
 * Main layout with navbar and sidebar for authenticated pages
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Container,
} from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import HistoryIcon from '@mui/icons-material/History';
import LogoutIcon from '@mui/icons-material/Logout';
import { useAuth } from '../../contexts/AuthContext';
import MasLogo from '../../assets/Mas_logo.png';

const DRAWER_WIDTH = 240;

export default function AppLayout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  // If user is logged in but Canva token is gone (e.g. after a scope change),
  // send them straight to the Canva OAuth flow to reconnect.
  useEffect(() => {
    if (user && user.canva_connected === false) {
      sessionStorage.setItem('canva_auth_pending', '1');
      const apiBase = window.location.hostname === 'localhost'
        ? 'http://localhost:8000/api'
        : '/api';
      window.location.href = `${apiBase}/auth/canva/authorize`;
    }
  }, [user]);

  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  // Initialise from sessionStorage immediately so navigation never shows "Never"
  const [lastSync, setLastSync] = useState(() => {
    const stored = sessionStorage.getItem('etl_last_sync');
    return stored ? new Date(stored) : null;
  });
  const [, setTick] = useState(0);

  const updateLastSync = (date) => {
    setLastSync(date);
    if (date) sessionStorage.setItem('etl_last_sync', date.toISOString());
  };

  useEffect(() => {
    const fetchSyncStatus = async () => {
      try {
        const token = localStorage.getItem('access_token');
        const res = await fetch('/api/dashboard/sync-status', {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          if (data.last_sync) {
            const dbTime = new Date(data.last_sync);
            // Only update if DB timestamp is newer than what we already show
            // — prevents "49 min ago" from overwriting a fresh "Just now"
            const stored = sessionStorage.getItem('etl_last_sync');
            const current = stored ? new Date(stored) : null;
            if (!current || dbTime > current) {
              updateLastSync(dbTime);
            }
          }
        }
      } catch (_) {}
    };
    fetchSyncStatus();

    // Tick every 60s to keep "X min ago" live without re-fetching
    const tickId = setInterval(() => setTick(t => t + 1), 60000);

    const onEtlStarted = () => updateLastSync(new Date());
    const onEtlCompleted = () => updateLastSync(new Date());

    window.addEventListener('etl:started', onEtlStarted);
    window.addEventListener('etl:completed', onEtlCompleted);

    return () => {
      clearInterval(tickId);
      window.removeEventListener('etl:started', onEtlStarted);
      window.removeEventListener('etl:completed', onEtlCompleted);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    sessionStorage.removeItem('canva_auth_pending');
    await logout();
    navigate('/login');
  };

  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
    { text: 'History', icon: <HistoryIcon />, path: '/history' },
  ];

  const formatSync = (date) => {
    if (!date) return 'Never';
    const now = new Date();
    const diffMins = Math.floor((now - date) / 60000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHrs = Math.floor(diffMins / 60);
    if (diffHrs < 24) return `${diffHrs}h ago`;
    return date.toLocaleDateString();
  };

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Toolbar sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 3 }}>
        <img
          src={MasLogo}
          alt="MAS Holdings Logo"
          style={{ width: '160px', height: 'auto' }}
        />
      </Toolbar>
      <Divider />
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => {
                navigate(item.path);
                setMobileOpen(false);
              }}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Box sx={{ flexGrow: 1 }} />
      <Divider />
      <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
        <SyncIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
        <Box>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.2 }}>
            Last data sync
          </Typography>
          <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.72rem' }}>
            {formatSync(lastSync)}
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      {/* App Bar */}
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${DRAWER_WIDTH}px)` },
          ml: { sm: `${DRAWER_WIDTH}px` },
          background: 'linear-gradient(90deg, #757575, #9E9E9E, #BDBDBD, #D32F2F, #BDBDBD, #9E9E9E, #757575)',
          backgroundSize: '300% 100%',
          animation: 'gradientShift 8s ease infinite',
          '@keyframes gradientShift': {
            '0%': { backgroundPosition: '0% 50%' },
            '50%': { backgroundPosition: '100% 50%' },
            '100%': { backgroundPosition: '0% 50%' },
          },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
            <Typography variant="h5" sx={{ fontWeight: 700, fontSize: '1.6rem' }}>
              {user?.full_name ? `Welcome back, ${user.full_name.split(' ')[0]}!` : 'Job Posting Automation'}
            </Typography>
          </Box>
          <IconButton color="inherit" onClick={handleProfileMenuOpen}>
            <Avatar sx={{ width: 40, height: 40, bgcolor: 'rgba(255, 255, 255, 0.2)', border: '2px solid rgba(255, 255, 255, 0.5)' }}>
              {user?.full_name?.charAt(0).toUpperCase() || user?.email?.charAt(0).toUpperCase() || 'U'}
            </Avatar>
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Profile Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        onClick={handleProfileMenuClose}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="subtitle2">Canva Connected</Typography>
          <Typography variant="body2" color="text.secondary">
            Automation Ready
          </Typography>
        </Box>
        <Divider />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          Logout
        </MenuItem>
      </Menu>

      {/* Sidebar Drawer */}
      <Box
        component="nav"
        sx={{ width: { sm: DRAWER_WIDTH }, flexShrink: { sm: 0 } }}
      >
        {/* Mobile Drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: DRAWER_WIDTH },
          }}
        >
          {drawer}
        </Drawer>
        {/* Desktop Drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: DRAWER_WIDTH },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${DRAWER_WIDTH}px)` },
          minHeight: '100vh',
          bgcolor: 'background.default',
        }}
      >
        <Toolbar />
        <Container maxWidth="xl" sx={{ mt: 2 }}>
          {children}
        </Container>
      </Box>
    </Box>
  );
}
